import { Injectable, Injector } from '@angular/core';
import { NgElement, WithProperties, createCustomElement } from '@angular/elements';
import { PopupComponent } from '../components/popup/popup.component';

import Map from 'ol/Map';
import Overlay from 'ol/Overlay';
// import OverlayPositioning from 'ol/OverlayPositioning.js';
// import { linear } from 'ol/easing';
import { unByKey } from 'ol/Observable';
import { MapService } from './map.service';
import { ViewService } from './view.service';

import { Subject, Subscription } from 'rxjs';
import { PopupData } from '../interfaces/popup-data'
import { fromExtent } from 'ol/geom/Polygon';


@Injectable({
  providedIn: 'root'
})

export class PopupService {

  private mapId: string;
  private map: Map;
  private popupEl: NgElement & WithProperties<PopupComponent>;
  private overlay: Overlay;
  private subscription: Subscription;
  public onPopupClose$ = new Subject<any>();
  public onPopupDestroy$ = new Subject<any>();
  public customEvent: Subject<any> = new Subject();
  static duration: number = 200;

  constructor(
    private injector: Injector,
    private mapService: MapService,
    private viewService: ViewService,
  ) {
    //create custom element from popupcomponent and register the custom element with the browser.
    const PopupElement = createCustomElement(PopupComponent, { injector });
    customElements.define('tt-popup', PopupElement);
  }

  add(popupData: PopupData) {
    const oldMapId = this.mapId ? this.mapId : 'main';
    const newMapId = popupData.mapId ? popupData.mapId : 'main';

    //switching to second map and popup was open on first map?
    let timeOutForPopupMapSwitch = 0;
    if (oldMapId !== newMapId && this.isPopupAlreadyOnMap()) {
      this.hide(true);
      timeOutForPopupMapSwitch = PopupService.duration + 100;
    }

    //there will be a timeout or not, depending on value of timeOutForPopupSwitch
    setTimeout(() => {

      this.mapId = newMapId;
      this.map = this.mapId === 'main' ? this.mapService.getMap() : this.mapService.getMap(this.mapId)

      if (!this.isPopupAlreadyOnMap()) {
        this.createPopupElement();
      }

      this.popupEl.state = 'closed';
      // Set props
      this.popupEl.popupData = popupData;

      //attr data-popupdatatype is used by methods hideInCaseOfType && isPopupWithType
      if (popupData.type && popupData.type.trim().length > 0) {
        this.popupEl.setAttribute("data-popupdatatype", popupData.type);
      } else {
        this.popupEl.setAttribute("data-popupdatatype", "");
      }

      setTimeout(() => {
        if (popupData.coordinates) {
          this.overlay.setPosition(popupData.coordinates);
          this.popupEl.state = 'opened';
        }
      }, PopupService.duration);

    }, timeOutForPopupMapSwitch);
  }

  private createPopupElement() {
    this.popupEl = document.createElement('tt-popup') as NgElement & WithProperties<PopupComponent>;
    //this.popupEl.classList.add('')
    this.popupEl.addEventListener('popupCloseEvent', () => this.onPopupClose(), { once: true });
    this.popupEl.addEventListener('zoomToFeatureEvent', (zoomtodata) => this.zoomToFeature(zoomtodata));
    this.popupEl.addEventListener('emitEvent', (obj) => this.emitCustomEvent(obj));
    this.overlay = new Overlay({
      id: 'popupoverlay',
      element: this.popupEl,
      autoPan: {
        animation: { duration: 250 },
        margin: 65
      },
      // autoPanAnimation: {
      //   duration: 250
      //   //,easing: linear
      // },
      // autoPanMargin: 55,
      insertFirst: true,
      offset: [0, -27],
      positioning: 'bottom-center'

    });
    this.map.addOverlay(this.overlay);
    //mapclick on/off marker listener to hide popup
    this.subscription = this.mapService.mapClickOnMarker$.subscribe((marker) => (!marker) ? this.hide() : (!marker.get('highlight') ? this.hide() : ''));
  }

  onPopupClose() {
    this.onPopupClose$.next(true);
    this.hide();
  }

  remove() {
    this.popupEl.state = 'closed';
    this.onPopupDestroy$.next(true);
    const oldMapId = this.mapId;
    // Set props
    setTimeout(() => {
      this.map.removeOverlay(this.overlay);
      this.subscription.unsubscribe();
      if (oldMapId === 'main') {
        unByKey(this.mapService.olMapClickListener);
      }
    }, PopupService.duration);
  }

  /**
   * this always closes the popup, no conditions (exception is when the pointerup event just happened 
   * after e.g. a long tap, then the hide request is ignored)
   * 
   * 
   * @param isHideForMapSwitch
   */

  hide(isHideForMapSwitch: boolean = false) {

    if (!isHideForMapSwitch
      && this.mapService.wasContextMenuEvent()
      && (this.mapService.contextMenuTapHoldEnd > -1 && (Date.now() - this.mapService.contextMenuTapHoldEnd) < 500)) {
      //pointer up event after a long tap should not be confused with a (no-)marker click event    
      console.log("ignored popup hide request, pointerup or contextmenu event")
      return;
    }

    //hide is remove 😉 
    this.remove();
  }

  /**
   * this only closes the popup if it was initialized with the same type (e.g. your component opened it with a specific popupData.type, 
   * but maybe another component already opened it again with another popupData.type)
   * 
   * @param className
   */
  hideInCaseOfType(types: string | string[]) {
    types = typeof types === "string" ? [types] : typeof types === "object" ? types : [];
    types.every((type) => {
      if (this.isPopupWithType(type)) {
        this.remove();
        return false;
      }
      return true;
    });
  }

  getPopupType() {
    return this.popupEl.type;
  }

  isPopupAlreadyOnMap(): boolean {
    if (this.map) {
      return (this.map.getOverlayById('popupoverlay') ? true : false)
      // return this.map.getOverlays().getArray().some(e => {console.dir(e.get('id'));e.id === 'popupoverlay'});
    }
    return false;
  }

  isPopupWithType(type: string): boolean {
    return this.isPopupAlreadyOnMap() && typeof type === "string" && type.trim().length > 0 && this.popupEl.getAttribute("data-popupdatatype") === type;
  }

  zoomToFeature(zoomtodata) {
    if (zoomtodata.detail.zoomextent) {
      this.viewService.zoomToFeatureExtent(fromExtent(zoomtodata.detail.zoomextent), [50, 50, 50, 50]);
    } else {
      this.viewService.zoomAndPan(zoomtodata.detail.coordinates[0], zoomtodata.detail.coordinates[1], zoomtodata.detail.zoomlevel || 17);
    }
  }

  emitCustomEvent(obj) {
    this.customEvent.next(obj);
  }
}